import { faChevronRight } from '@fortawesome/pro-regular-svg-icons';
import { faArrowUpRightFromSquare } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { HoverCard, HoverCardContent, HoverCardTrigger, sourTwMerge } from '@plaidev/sour-react';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import type { GlobalNaviUserGrants, Platform } from '../contract/index.js';
import { ADMIN_UI_STRIPE_STYLE, PopoverMenuContent, PopoverMenuItem } from './popoverMenu.js';

export type GlobalNaviGrantClientConsent = {
  confirmed: boolean;
  evidence: string;
};

export type GlobalNaviGrantActions = {
  pendingGrantKey?: 'systemAdmin' | 'piiAccess';
  mutatingGrantKey?: 'systemAdmin' | 'piiAccess';
  isMutating?: boolean;
  onRequest: (params: { grantKey: 'systemAdmin' | 'piiAccess'; reason?: string; clientConsent?: GlobalNaviGrantClientConsent }) => void;
  onRevoke: (params: { grantKey: 'systemAdmin' | 'piiAccess' }) => void;
};

type AdminMenuSectionProps = {
  grants: GlobalNaviUserGrants;
  selfPlatform: Platform;
  grantActions?: GlobalNaviGrantActions;
  systemAdminPageHref: string;
  /**
   * 親ポップオーバー（ユーザーメニュー）の開閉状態。
   * サブメニュー（HoverCard）の content は body へ portal されるため、親ポップオーバーが
   * 閉じても（＝global-navi が折りたたまれても）サブメニューだけ画面に残ることがある。
   * これを防ぐため HoverCard を制御し、親が閉じている間は絶対に開かないようにする。
   */
  parentOpen: boolean;
};

const hasGrantMenu = (grants: GlobalNaviUserGrants, pendingGrantKey?: GlobalNaviGrantActions['pendingGrantKey']): boolean => {
  return (
    grants.isSystemAdminEligible ||
    grants.hasActiveSystemAdmin ||
    pendingGrantKey === 'systemAdmin' ||
    grants.isPiiEligible ||
    grants.hasActivePiiAccess ||
    pendingGrantKey === 'piiAccess'
  );
};

export function AdminMenuSection({
  grants,
  selfPlatform,
  grantActions,
  systemAdminPageHref,
  parentOpen,
}: AdminMenuSectionProps): ReactNode {
  // サブメニューの開閉は HoverCard 内蔵のロジックに任せず、自前の hover 状態で完全制御する。
  // HoverCard の pointerdown-outside / focus / close タイマーは制御 open と競合し、
  // 「トリガーをクリックすると閉じ、ポインタが留まると pointerenter が来ず再オープンできない」
  // というデッドロックを生む。閉じるのは「トリガーとコンテンツの両方から離れた時」だけに限定する。
  const [submenuOpen, setSubmenuOpen] = useState(false);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const clearCloseTimer = () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = undefined;
  };
  const openSubmenu = () => {
    clearCloseTimer();
    setSubmenuOpen(true);
  };
  // トリガー↔コンテンツ間のポインタ移動で一瞬 leave するため、閉じるのは遅延させる。
  const closeSubmenuSoon = () => {
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => setSubmenuOpen(false), 150);
  };

  // 親ポップオーバーが閉じたらサブメニューも即座に閉じる（body へ portal されるため取り残される）。
  useEffect(() => {
    if (!parentOpen) {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
      closeTimerRef.current = undefined;
      setSubmenuOpen(false);
    }
  }, [parentOpen]);
  // アンマウント時にタイマーを掃除する。
  useEffect(
    () => () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    },
    [],
  );

  if (!hasGrantMenu(grants, grantActions?.pendingGrantKey)) return null;

  const isKarte = selfPlatform === 'karte';
  const isMutating = grantActions?.isMutating ?? false;
  const pendingGrantKey = grantActions?.pendingGrantKey;
  const mutatingGrantKey = grantActions?.mutatingGrantKey;

  const showSystemAdmin = grants.isSystemAdminEligible || grants.hasActiveSystemAdmin || pendingGrantKey === 'systemAdmin';
  const showPii = grants.isPiiEligible || grants.hasActivePiiAccess || pendingGrantKey === 'piiAccess';

  const renderSystemAdminPageItem = (params: { isActive: boolean; isEligible: boolean }): ReactNode => {
    const { isActive, isEligible } = params;

    const menuItem = (disabled: boolean): ReactNode => (
      <PopoverMenuItem disabled={disabled}>
        <span className="flex-1 truncate">システム管理者画面</span>
        <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="shrink-0 text-[10px] text-muted" />
      </PopoverMenuItem>
    );

    if (isActive) {
      return (
        <a href={systemAdminPageHref} target="_blank" rel="noopener noreferrer">
          {menuItem(false)}
        </a>
      );
    }

    if (isEligible && !isActive) {
      return menuItem(true);
    }

    return null;
  };

  const renderGrantItem = (params: {
    grantKey: 'systemAdmin' | 'piiAccess';
    activeLabel: string;
    revokeLabel: string;
    requestLabel: string;
    pendingLabel: string;
    requestingLabel: string;
    revokingLabel: string;
    isActive: boolean;
    isEligible: boolean;
  }): ReactNode => {
    const isPending = pendingGrantKey === params.grantKey;
    const isMutatingThis = mutatingGrantKey === params.grantKey;
    const isRequesting = isMutatingThis && !isPending && !params.isActive;
    const isRevoking = isMutatingThis && params.isActive;
    const disabled = isKarte || isMutating || isPending || (!params.isActive && !params.isEligible);

    const label = params.isActive
      ? isRevoking
        ? params.revokingLabel
        : params.revokeLabel
      : isPending
        ? params.pendingLabel
        : isRequesting
          ? params.requestingLabel
          : params.requestLabel;

    return (
      <PopoverMenuItem
        disabled={disabled}
        onClick={
          disabled || !grantActions
            ? undefined
            : params.isActive
              ? () => grantActions.onRevoke({ grantKey: params.grantKey })
              : () => grantActions.onRequest({ grantKey: params.grantKey })
        }
      >
        {label}
      </PopoverMenuItem>
    );
  };

  return (
    // open は自前状態で完全制御し、Radix 内蔵の開閉（pointerdown-outside / focus / タイマー）が
    // 状態を書き換えないよう onOpenChange は無効化する。開閉は下記のハンドラだけが決める。
    <HoverCard open={parentOpen && submenuOpen} onOpenChange={() => undefined}>
      <HoverCardTrigger asChild>
        <div
          role="button"
          tabIndex={0}
          onPointerEnter={openSubmenu}
          onPointerLeave={closeSubmenuSoon}
          onFocus={openSubmenu}
          onBlur={closeSubmenuSoon}
          // クリックでも開く（タップ環境や、閉じた直後に同じ位置をクリックした場合の保険）。
          onClick={openSubmenu}
          // キーボード操作（Enter / Space）でも開けるようにする。
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              openSubmenu();
            }
          }}
          className={sourTwMerge(
            'flex items-center gap-3 h-9 w-full font-medium px-4 outline-none transition-colors text-default text-sm cursor-pointer hover:bg-action-subtle-hover',
          )}
          style={ADMIN_UI_STRIPE_STYLE}
        >
          <span className="flex-1 truncate">管理用メニュー</span>
          <FontAwesomeIcon icon={faChevronRight} className="shrink-0 text-xs text-muted" />
        </div>
      </HoverCardTrigger>
      <HoverCardContent
        side="right"
        align="start"
        // sideOffset は sour 内部で親メニューとの間の余白（padding）になる。
        // 階層メニューは隙間なく隣接させたいので 0 にする（閉じ対策は自前の遅延クローズで担保済み）。
        sideOffset={0}
        className="p-0"
        onPointerEnter={openSubmenu}
        onPointerLeave={closeSubmenuSoon}
        onFocus={openSubmenu}
        onBlur={closeSubmenuSoon}
      >
        <PopoverMenuContent>
          {showSystemAdmin && (
            <>
              {renderGrantItem({
                grantKey: 'systemAdmin',
                isActive: grants.hasActiveSystemAdmin,
                isEligible: grants.isSystemAdminEligible,
                activeLabel: 'システム管理者権限を無効化',
                revokeLabel: 'システム管理者権限を無効化',
                requestLabel: 'システム管理者権限をリクエスト',
                pendingLabel: 'システム管理者権限の承認待ち',
                requestingLabel: 'システム管理者権限をリクエスト中',
                revokingLabel: 'システム管理者権限を無効化中',
              })}
              {renderSystemAdminPageItem({
                isActive: grants.hasActiveSystemAdmin,
                isEligible: grants.isSystemAdminEligible,
              })}
            </>
          )}
          {showPii &&
            renderGrantItem({
              grantKey: 'piiAccess',
              isActive: grants.hasActivePiiAccess,
              isEligible: grants.isPiiEligible,
              activeLabel: '個人情報アクセスを無効化',
              revokeLabel: '個人情報アクセスを無効化',
              requestLabel: '個人情報アクセスをリクエスト',
              pendingLabel: '個人情報アクセスの承認待ち',
              requestingLabel: '個人情報アクセスをリクエスト中',
              revokingLabel: '個人情報アクセスを無効化中',
            })}
        </PopoverMenuContent>
      </HoverCardContent>
    </HoverCard>
  );
}
